import { ClockStopwatch, LogIn01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useState } from 'react';
import LogoSmall from 'src/assets/img/logo/logo-icon.svg';
import LogoWhite from 'src/assets/img/logo/Wal-full-logo-white.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import { ROUTES } from 'src/constants/routes';
import { getPortalOrigin } from 'src/utils/portalOrigin';

/**
 * ExpiryBadge — live "Link expires in Xh Ym" pill driven by `tokenExpiresAt`.
 * Re-evaluates every 30s; switches to "Link expired" once the timestamp passes.
 *
 * Exported so the share-link page can render it above the quote detail
 * (alongside the Login button) instead of inside the dark top bar.
 */
export const ExpiryBadge: React.FC<{ tokenExpiresAt: string | null }> = ({ tokenExpiresAt }) => {
    const expiryMs = useMemo(() => {
        if (!tokenExpiresAt) return null;
        const ms = new Date(tokenExpiresAt).getTime();

        return Number.isFinite(ms) ? ms : null;
    }, [tokenExpiresAt]);

    const [now, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        if (expiryMs === null) return;
        const tick = () => setNow(Date.now());
        const interval = window.setInterval(tick, 30_000);

        return () => window.clearInterval(interval);
    }, [expiryMs]);

    if (expiryMs === null) {
        return <span className="px-2.5 py-1 text-xs rounded-md bg-gray800 text-gray100 custom-loading min-w-[140px]">&nbsp;</span>;
    }

    const remainingMs = expiryMs - now;

    if (remainingMs <= 0) {
        return <span className="px-2.5 py-1 text-xs rounded-md bg-danger100 text-danger700 font-medium">Link expired</span>;
    }

    const totalMinutes = Math.floor(remainingMs / 60_000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    let label: string;

    if (days > 0) {
        label = `Link expires in ${days}d ${hours}h`;
    } else if (hours > 0) {
        label = `Link expires in ${hours}h ${minutes}m`;
    } else {
        label = `Link expires in ${minutes}m`;
    }

    return (
        <span className="flex items-center justify-center lg:justify-start gap-1.5 w-full lg:w-auto px-3 py-1 lg:px-2 lg:py-1 text-xs font-medium whitespace-nowrap rounded-md lg:rounded-full border border-[#FEDF89] bg-[#FFFAEB] text-[#B54708] !hidden">
            <ClockStopwatch className="text-[#DC6803] h-3 w-3" />
            <span className="">{label}</span>
        </span>
    );
};

/**
 * SharedPageTopBar — minimal black header for public share-link pages.
 *
 * Renders the WAL logo on the left and a "Login" icon button on the right.
 * Login opens the portal's own origin: the public tracking page runs on another hostname (track.).
 *
 * `centerContent` is the public tracking page's WAL ID search. With it the bar takes the portal
 * TopHeader's layout (logo, centred search, actions; the small logo on phones), inside the same
 * frame as the page below so the logo and Login sit on the content's edges (Lucas, 2026-10-01).
 * Without `centerContent` the bar renders exactly as before.
 */
const LOGIN_LABEL = 'Login';

const SharedPageTopBar: React.FC<{ centerContent?: React.ReactNode }> = ({ centerContent }) => {
    // Icon-only button, so what it does is only discoverable on hover — the aria-label covers screen
    // readers but says nothing to a sighted visitor on the public tracking page (client, 2026-10-09).
    // Same literal as ariaLabel on purpose: one word, two surfaces, kept identical.
    const loginButton = (
        <TooltipCmp message={LOGIN_LABEL}>
            <ButtonCmp
                className="btn-outline-primary !border-opacity-100 !p-1.5 max-md:!p-[5px]"
                size="square-sm"
                ariaLabel={LOGIN_LABEL}
                icon={<LogIn01 className="w-4 h-4 max-md:w-3.5 max-md:h-3.5 text-primary700" />}
                onClick={() => {
                    window.location.href = `${getPortalOrigin()}${ROUTES.LOGIN}`;
                }}
            >
                {''}
            </ButtonCmp>
        </TooltipCmp>
    );

    if (centerContent) {
        return (
            <header className="flex flex-col w-full bg-utilityBlack700 min-h-[56px] items-start justify-center sticky top-0 z-50">
                <div className="w-full lg:px-4">
                    {/* Inside lg:px-4 + max-w-[1480px], px-5 on lg puts the logo and Login 36 px in, on the
                        card edges of the globe and the live tracking page (lg:px-4 + lg:p-5); below lg the
                        card runs full width and the bar keeps TopHeader's padding. */}
                    <div className="flex items-center justify-between gap-x-2 py-[9px] max-w-[1480px] w-full mx-auto px-3 mdm:px-5">
                        <div className="flex-1">
                            <a href="/" aria-label="Western Alliance Logistics" className="flex w-fit">
                                <img src={LogoWhite} alt="logo-light" className="h-[28px] object-contain md:inline-block hidden min-w-[187px]" />
                                <img src={LogoSmall} alt="logo-light" className="h-[28px] max-w-[40px] object-contain md:hidden z-[7] relative" />
                            </a>
                        </div>
                        <div className="flex gap-x-2 w-[calc(100%-340px)] max-sm:w-full justify-center">{centerContent}</div>
                        <div className="flex items-center gap-x-3 max-sm:gap-x-2 flex-1 justify-end">{loginButton}</div>
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header className="flex flex-col w-full bg-utilityBlack700 min-h-[56px] items-start justify-center sticky top-0 z-50">
            <div className="flex items-center justify-between gap-x-2 py-[9px] mdm:px-5 px-4 max-md:px-[14px] w-full">
                <div className="flex-1">
                    <a href="/" aria-label="Western Alliance Logistics" className="flex w-fit">
                        <img src={LogoWhite} alt="logo-light" className="h-[28px] object-contain md:inline-block hidden max-w-[187px]" />
                        <img src={LogoWhite} alt="logo-light" className="h-[24px] max-w-[184px] object-contain md:hidden z-[7] relative" />
                    </a>
                </div>
                {loginButton}
            </div>
        </header>
    );
};

export default SharedPageTopBar;
